import React, { useEffect, useRef } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    Chip, 
    Divider,
    Alert,
    useTheme
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import { azureMapsManager } from '../../../services/azure-maps-manager';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LanguageIcon from '@mui/icons-material/Language';
import HomeIcon from '@mui/icons-material/Home';
import FlagIcon from '@mui/icons-material/Flag';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import WifiIcon from '@mui/icons-material/Wifi';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';

// Azure Maps type declarations
declare global {
    interface Window {
        atlas: any;
        azureMapsLoading?: boolean;
    }
}

interface GeolocationTabV2Props {
    consentData: SandboxConsentDetails;
}

// Azure Maps component using the global manager
const SimpleAzureMapComponent: React.FC<{
    latitude: number;
    longitude: number;
    accuracy?: number | null;
    source?: string | null;
}> = ({ latitude, longitude, accuracy, source }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const instanceIdRef = useRef<string>(`map-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const [mapStatus, setMapStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading');
    const [mapStyle, setMapStyle] = React.useState<'street' | 'satellite'>('street');

    useEffect(() => {
        const azureMapsKey = process.env.REACT_APP_AZURE_MAPS_KEY;
        if (!azureMapsKey || !mapRef.current) {
            console.log('Azure Maps key not available, using fallback');
            setMapStatus('error');
            return;
        }

        const instanceId = instanceIdRef.current;
        console.log(`Initializing Azure Maps for instance: ${instanceId}`);

        // Register this instance with the manager
        azureMapsManager.registerInstance(instanceId);

        const loadAndInitializeMap = async () => {
            try {
                // Use the global manager to load Azure Maps
                await azureMapsManager.loadAzureMaps();
                console.log(`Azure Maps loaded for instance: ${instanceId}`);
                
                // Initialize the map
                initializeMap();
            } catch (error) {
                console.error(`Error loading Azure Maps for instance ${instanceId}:`, error);
                setMapStatus('error');
            }
        };

        const initializeMap = () => {
            if (!window.atlas || !mapRef.current) {
                console.log(`Azure Maps not available or map ref not ready for instance: ${instanceId}`);
                setMapStatus('error');
                return;
            }

            try {
                console.log(`Creating Azure Map instance: ${instanceId}`);
                
                // Create map instance with dynamic styling
                const map = new window.atlas.Map(mapRef.current, {
                    authOptions: {
                        authType: 'subscriptionKey',
                        subscriptionKey: azureMapsKey
                    },
                    center: [longitude, latitude],
                    zoom: accuracy && accuracy < 100 ? 14 : accuracy && accuracy < 1000 ? 12 : 10,
                    style: mapStyle === 'street' ? 'road_shaded_relief' : 'satellite_road_labels',
                    view: 'Auto'
                });

                mapInstanceRef.current = map;
                setMapStatus('loaded');

                // Wait for map to be ready
                map.events.add('ready', () => {
                    console.log(`Azure Map ready for instance: ${instanceId}, adding enhanced features...`);
                    
                    // Add map controls for better user experience
                    map.controls.add(new window.atlas.control.ZoomControl(), {
                        position: 'top-right'
                    });
                    map.controls.add(new window.atlas.control.CompassControl(), {
                        position: 'top-right'
                    });
                    
                    // Add custom style toggle button
                    const styleToggleButton = document.createElement('button');
                    styleToggleButton.innerHTML = mapStyle === 'street' ? '🛰️' : '🗺️';
                    styleToggleButton.title = mapStyle === 'street' ? 'Switch to Satellite View' : 'Switch to Street View';
                    styleToggleButton.style.cssText = `
                        position: absolute;
                        top: 10px;
                        left: 10px;
                        width: 40px;
                        height: 40px;
                        background: white;
                        border: 1px solid #ccc;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        z-index: 1000;
                        transition: all 0.2s ease;
                    `;
                    
                    styleToggleButton.addEventListener('mouseenter', () => {
                        styleToggleButton.style.backgroundColor = '#f0f0f0';
                        styleToggleButton.style.transform = 'scale(1.05)';
                    });
                    
                    styleToggleButton.addEventListener('mouseleave', () => {
                        styleToggleButton.style.backgroundColor = 'white';
                        styleToggleButton.style.transform = 'scale(1)';
                    });
                    
                    styleToggleButton.addEventListener('click', () => {
                        const newStyle = mapStyle === 'street' ? 'satellite' : 'street';
                        setMapStyle(newStyle);
                        
                        // Update map style
                        map.setStyle(newStyle === 'street' ? 'road_shaded_relief' : 'satellite_road_labels');
                        
                        // Update button
                        styleToggleButton.innerHTML = newStyle === 'street' ? '🛰️' : '🗺️';
                        styleToggleButton.title = newStyle === 'street' ? 'Switch to Satellite View' : 'Switch to Street View';
                    });
                    
                    if (mapRef.current) {
                        mapRef.current.appendChild(styleToggleButton);
                    }
                    
                    // Create enhanced marker with better styling
                    const markerHtml = `
                        <div style="
                            background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
                            width: 24px;
                            height: 24px;
                            border-radius: 50%;
                            border: 4px solid white;
                            box-shadow: 0 4px 12px rgba(0,120,212,0.4), 0 2px 4px rgba(0,0,0,0.2);
                            position: relative;
                            animation: pulse 2s infinite;
                        ">
                            <div style="
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                width: 8px;
                                height: 8px;
                                background: white;
                                border-radius: 50%;
                            "></div>
                        </div>
                        <style>
                            @keyframes pulse {
                                0% { transform: scale(1); }
                                50% { transform: scale(1.1); }
                                100% { transform: scale(1); }
                            }
                        </style>
                    `;
                    
                    const marker = new window.atlas.HtmlMarker({
                        htmlContent: markerHtml,
                        position: [longitude, latitude],
                        anchor: 'center'
                    });

                    map.markers.add(marker);
                    
                    // Add accuracy circle for GPS coordinates
                    if (accuracy && source === 'browser' && accuracy < 10000) {
                        console.log(`Adding accuracy circle with radius: ${accuracy}m`);
                        
                        // Create data source for accuracy circle
                        const dataSource = new window.atlas.source.DataSource();
                        map.sources.add(dataSource);

                        // Create circle geometry
                        const circle = new window.atlas.Shape(new window.atlas.data.Circle([longitude, latitude], accuracy));
                        dataSource.add(circle);

                        // Add polygon layer for the circle with enhanced styling
                        const polygonLayer = new window.atlas.layer.PolygonLayer(dataSource, {
                            fillColor: 'rgba(0, 120, 212, 0.15)',
                            strokeColor: '#0078d4',
                            strokeWidth: 3,
                            strokeDashArray: [5, 5]
                        });
                        map.layers.add(polygonLayer);
                        console.log(`Accuracy circle added for instance: ${instanceId}`);
                    }

                    // Add popup with enhanced styling
                    const popup = new window.atlas.Popup({
                        content: `
                            <div style="
                                padding: 16px; 
                                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                                background: white;
                                border-radius: 8px;
                                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                                min-width: 200px;
                            ">
                                <div style="
                                    display: flex;
                                    align-items: center;
                                    margin-bottom: 12px;
                                    padding-bottom: 8px;
                                    border-bottom: 1px solid #e0e0e0;
                                ">
                                    <div style="
                                        width: 12px;
                                        height: 12px;
                                        background: #0078d4;
                                        border-radius: 50%;
                                        margin-right: 8px;
                                    "></div>
                                    <strong style="color: #323130; font-size: 14px;">Location Details</strong>
                                </div>
                                <div style="color: #605e5c; font-size: 13px; line-height: 1.5;">
                                    <div style="margin-bottom: 4px;"><strong>Lat:</strong> ${latitude.toFixed(6)}</div>
                                    <div style="margin-bottom: 4px;"><strong>Lng:</strong> ${longitude.toFixed(6)}</div>
                                    ${accuracy ? `<div style="margin-bottom: 4px;"><strong>Accuracy:</strong> ${accuracy < 1000 ? Math.round(accuracy) + 'm' : (accuracy/1000).toFixed(1) + 'km'}</div>` : ''}
                                    <div><strong>Source:</strong> ${source === 'browser' ? 'GPS' : source === 'ip' ? 'IP Geolocation' : 'Unknown'}</div>
                                </div>
                            </div>
                        `,
                        position: [longitude, latitude],
                        pixelOffset: [0, -20]
                    });

                    // Add click event to marker to show popup
                    map.events.add('click', marker, () => {
                        popup.open(map);
                    });
                    
                    console.log(`Enhanced features added successfully for instance: ${instanceId}`);
                });

            } catch (error) {
                console.error(`Error initializing map for instance ${instanceId}:`, error);
                setMapStatus('error');
            }
        };

        loadAndInitializeMap();

        // Cleanup - dispose map instance and unregister from manager
        return () => {
            const instanceId = instanceIdRef.current;
            console.log(`Cleaning up Azure Maps instance: ${instanceId}`);
            
            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.dispose();
                    console.log(`Map instance disposed for: ${instanceId}`);
                } catch (error) {
                    console.warn(`Error disposing map for instance ${instanceId}:`, error);
                }
            }
            
            // Unregister from manager
            azureMapsManager.unregisterInstance(instanceId);
        };
    }, [latitude, longitude, accuracy, source, mapStyle]);

    return (
        <div
            ref={mapRef}
            style={{
                width: '100%',
                height: '300px',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e1e5e9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'box-shadow 0.3s ease'
            }}
        >
            {mapStatus === 'loading' && (
                <div style={{ 
                    textAlign: 'center', 
                    color: '#0078d4',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #e1e5e9',
                        borderTop: '3px solid #0078d4',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <div style={{ fontWeight: '500' }}>Loading Azure Maps...</div>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            )}
            {mapStatus === 'error' && (
                <div style={{ 
                    textAlign: 'center', 
                    color: '#d32f2f',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: '#ffebee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                    }}>⚠️</div>
                    <div style={{ fontWeight: '500' }}>Failed to load Azure Maps</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Check console for details</div>
                </div>
            )}
        </div>
    );
};

const GeolocationTabV2: React.FC<GeolocationTabV2Props> = ({ consentData }) => {
    const theme = useTheme();
    const { geolocation, collectionPoint } = consentData;
    
    const isTrackingEnabled = collectionPoint.trackGeolocation;
    const hasCoordinates = geolocation.latitude !== null && geolocation.longitude !== null;
    const hasAddress = geolocation.countryCode || geolocation.region || geolocation.city;
    const hasIP = geolocation.ipAddress !== null;
    const hasAccuracy = geolocation.accuracy !== null;
    const hasSource = geolocation.geolocationSource !== null;
    const hasTimestamp = geolocation.geolocationTimestamp !== null;

    // Format coordinates for display
    const formatCoordinate = (coord: number | null, type: 'lat' | 'lng'): string => {
        if (coord === null) return 'Not available';
        return `${coord.toFixed(6)}°${type === 'lat' ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W')}`;
    };

    // Convert country code to full country name
    const getCountryName = (countryCode: string | null): string => {
        if (!countryCode) return 'Not available';
        
        const countryMap: { [key: string]: string } = {
            'IN': 'India', 'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada',
            'AU': 'Australia', 'DE': 'Germany', 'FR': 'France', 'IT': 'Italy', 'ES': 'Spain',
            'NL': 'Netherlands', 'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria',
            'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland',
            'CZ': 'Czech Republic', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria',
            'GR': 'Greece', 'PT': 'Portugal', 'IE': 'Ireland', 'LU': 'Luxembourg',
            'MT': 'Malta', 'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania',
            'SI': 'Slovenia', 'SK': 'Slovakia', 'HR': 'Croatia', 'JP': 'Japan', 'KR': 'South Korea',
            'CN': 'China', 'SG': 'Singapore', 'HK': 'Hong Kong', 'TW': 'Taiwan', 'TH': 'Thailand',
            'MY': 'Malaysia', 'ID': 'Indonesia', 'PH': 'Philippines', 'VN': 'Vietnam',
            'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia',
            'PE': 'Peru', 'ZA': 'South Africa', 'EG': 'Egypt', 'NG': 'Nigeria', 'KE': 'Kenya',
            'MA': 'Morocco', 'TN': 'Tunisia', 'DZ': 'Algeria', 'LY': 'Libya', 'SD': 'Sudan',
            'ET': 'Ethiopia', 'GH': 'Ghana', 'UG': 'Uganda', 'TZ': 'Tanzania', 'ZM': 'Zambia',
            'ZW': 'Zimbabwe', 'BW': 'Botswana', 'NA': 'Namibia', 'MW': 'Malawi', 'MZ': 'Mozambique',
            'MG': 'Madagascar', 'MU': 'Mauritius', 'SC': 'Seychelles', 'RU': 'Russia',
            'UA': 'Ukraine', 'BY': 'Belarus', 'KZ': 'Kazakhstan', 'UZ': 'Uzbekistan',
            'KG': 'Kyrgyzstan', 'TJ': 'Tajikistan', 'TM': 'Turkmenistan', 'AF': 'Afghanistan',
            'PK': 'Pakistan', 'BD': 'Bangladesh', 'LK': 'Sri Lanka', 'NP': 'Nepal', 'BT': 'Bhutan',
            'MV': 'Maldives', 'MM': 'Myanmar', 'LA': 'Laos', 'KH': 'Cambodia', 'BN': 'Brunei',
            'TL': 'East Timor', 'FJ': 'Fiji', 'PG': 'Papua New Guinea', 'NC': 'New Caledonia',
            'VU': 'Vanuatu', 'SB': 'Solomon Islands', 'TO': 'Tonga', 'WS': 'Samoa', 'KI': 'Kiribati',
            'TV': 'Tuvalu', 'NR': 'Nauru', 'PW': 'Palau', 'FM': 'Micronesia', 'MH': 'Marshall Islands',
            'NZ': 'New Zealand', 'IS': 'Iceland', 'GL': 'Greenland', 'FO': 'Faroe Islands',
            'SJ': 'Svalbard and Jan Mayen', 'AD': 'Andorra', 'LI': 'Liechtenstein', 'MC': 'Monaco',
            'SM': 'San Marino', 'VA': 'Vatican City', 'GI': 'Gibraltar', 'IM': 'Isle of Man',
            'JE': 'Jersey', 'GG': 'Guernsey', 'AX': 'Åland Islands', 'TR': 'Turkey', 'IL': 'Israel',
            'PS': 'Palestine', 'JO': 'Jordan', 'LB': 'Lebanon', 'SY': 'Syria', 'IQ': 'Iraq',
            'IR': 'Iran', 'SA': 'Saudi Arabia', 'AE': 'United Arab Emirates', 'QA': 'Qatar',
            'BH': 'Bahrain', 'KW': 'Kuwait', 'OM': 'Oman', 'YE': 'Yemen', 'MN': 'Mongolia',
            'KP': 'North Korea', 'MO': 'Macau'
        };
        
        return countryMap[countryCode.toUpperCase()] || countryCode.toUpperCase();
    };

    // Format accuracy for display
    const formatAccuracy = (accuracy: number | null): string => {
        if (accuracy === null) return 'Not available';
        if (accuracy < 1000) {
            return `${Math.round(accuracy)} meters`;
        } else {
            return `${(accuracy / 1000).toFixed(1)} km`;
        }
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp: string | null): string => {
        if (!timestamp) return 'Not available';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return 'Invalid timestamp';
        }
    };

    // Get source display information
    const getSourceInfo = (source: string | null) => {
        if (!source) return { label: 'Unknown', icon: <PublicIcon />, color: 'default' as const };
        
        switch (source) {
            case 'browser':
                return { 
                    label: 'GPS (Browser)', 
                    icon: <GpsFixedIcon />, 
                    color: 'success' as const,
                    description: 'High-accuracy GPS coordinates from device'
                };
            case 'ip':
                return { 
                    label: 'IP Geolocation', 
                    icon: <WifiIcon />, 
                    color: 'warning' as const,
                    description: 'Approximate location from IP address - may show ISP routing location rather than actual user location'
                };
            default:
                return { 
                    label: source, 
                    icon: <PublicIcon />, 
                    color: 'default' as const,
                    description: 'Location data source'
                };
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LocationOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h5" component="h2" gutterBottom>
                                Geolocation Information
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Location data collected during consent submission
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* Status Chip - Moved to the right */}
                    <Chip
                        icon={isTrackingEnabled ? <CheckCircleIcon /> : <CancelIcon />}
                        label={isTrackingEnabled ? 'Geolocation Tracking Enabled' : 'Geolocation Tracking Disabled'}
                        color={isTrackingEnabled ? 'success' : 'error'}
                        variant="outlined"
                    />
                </Box>
            </Box>

            {/* Show location information only if tracking is enabled */}
            {isTrackingEnabled ? (
                <Grid container spacing={3}>
                {/* Left Column - Location Data */}
                <Grid item xs={12} md={6}>
                    {/* Coordinates Card */}
                    <Paper sx={{ p: 3, mb: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <LocationOnIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                                Coordinates
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Latitude
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                    {formatCoordinate(geolocation.latitude, 'lat')}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Longitude
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                    {formatCoordinate(geolocation.longitude, 'lng')}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Accuracy and Source Information */}
                        {hasCoordinates && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {hasAccuracy && (
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <GpsNotFixedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Accuracy
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                            {formatAccuracy(geolocation.accuracy)}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {hasSource && (
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            {getSourceInfo(geolocation.geolocationSource).icon}
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                Source:
                                            </Typography>
                                            <Chip
                                                icon={getSourceInfo(geolocation.geolocationSource).icon}
                                                label={getSourceInfo(geolocation.geolocationSource).label}
                                                color={getSourceInfo(geolocation.geolocationSource).color}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        )}

                        {/* Timestamp Information */}
                        {hasTimestamp && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                            Location Obtained:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                            {formatTimestamp(geolocation.geolocationTimestamp)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {/* Enhanced Map Preview */}
                        {hasCoordinates ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Interactive Location Map
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 300,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        position: 'relative'
                                    }}
                                >
                                    <SimpleAzureMapComponent
                                        latitude={geolocation.latitude!}
                                        longitude={geolocation.longitude!}
                                        accuracy={geolocation.accuracy}
                                        source={geolocation.geolocationSource}
                                    />
                                </Box>
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Interactive map with street/satellite toggle
                                    </Typography>
                                    {hasAccuracy && geolocation.geolocationSource === 'browser' && (
                                        <Chip
                                            label={`Accuracy: ${formatAccuracy(geolocation.accuracy)}`}
                                            color="info"
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 300,
                                    borderRadius: 1,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'background.default'
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Map preview not available
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Address Information Card */}
                    <Paper sx={{ p: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <HomeIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                                Address Information
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        {hasAddress ? (
                            <Grid container spacing={2}>
                                {geolocation.countryCode && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                Country:
                                            </Typography>
                                            <Typography variant="body1" sx={{ mr: 1 }}>
                                                {getCountryName(geolocation.countryCode)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                ({geolocation.countryCode.toUpperCase()})
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                
                                {geolocation.region && (
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                Region:
                                            </Typography>
                                            <Typography variant="body1">
                                                {geolocation.region}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                
                                {geolocation.city && (
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                City:
                                            </Typography>
                                            <Typography variant="body1">
                                                {geolocation.city}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Address information not available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Right Column - Technical Information */}
                <Grid item xs={12} md={6}>
                    {/* IP Address Card */}
                    <Paper sx={{ p: 3, mb: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <LanguageIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                                IP Address
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        {hasIP ? (
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Server-Extracted IP Address
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                                    {geolocation.ipAddress}
                                </Typography>
                                <Chip
                                    icon={<SecurityIcon />}
                                    label="Server-side extraction (secure)"
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                IP address not collected
                            </Typography>
                        )}
                    </Paper>

                    {/* Data Collection Details Card */}
                    <Paper sx={{ p: 3, mb: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PublicIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                                Data Collection Details
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ mb: 1 }}>
                                    {hasSource ? (
                                        <Chip
                                            icon={getSourceInfo(geolocation.geolocationSource).icon}
                                            label={getSourceInfo(geolocation.geolocationSource).label}
                                            color={getSourceInfo(geolocation.geolocationSource).color}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Chip
                                            label="Method unknown"
                                            color="default"
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Collection Method: {hasSource ? getSourceInfo(geolocation.geolocationSource).description : 'Unknown method'}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Box sx={{ mb: 1 }}>
                                    {hasAccuracy ? (
                                        <Chip
                                            label={formatAccuracy(geolocation.accuracy)}
                                            color={geolocation.accuracy! < 100 ? 'success' : 
                                                   geolocation.accuracy! < 1000 ? 'warning' : 'error'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Chip
                                            label="Unknown accuracy"
                                            color="default"
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Data Quality: {hasAccuracy ? 
                                        (geolocation.accuracy! < 100 ? 'High precision' : 
                                         geolocation.accuracy! < 1000 ? 'Good precision' : 'Approximate location') 
                                        : 'Unknown accuracy level'}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Box sx={{ mb: 1 }}>
                                    <Chip
                                        label="DPDPA Compliant"
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Privacy Compliance: DPDPA compliant data collection
                                </Typography>
                            </Grid>
                        </Grid>
                        
                        {/* IP Geolocation Warning */}
                        {hasSource && geolocation.geolocationSource === 'ip' && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>IP Geolocation Limitation:</strong> The location shown may reflect your ISP's routing infrastructure rather than your actual physical location. 
                                    This is common with mobile networks, VPNs, or when ISPs route traffic through different cities.
                                </Typography>
                            </Alert>
                        )}
                    </Paper>

                    {/* Privacy & Compliance Card */}
                    <Paper sx={{ p: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <SecurityIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                                Privacy & Compliance
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Purpose
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Consent verification and compliance
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Retention
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    As per consent retention policy
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Security
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Encrypted storage and transmission
                                </Typography>
                                <Chip
                                    label="Encrypted"
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            ) : (
                /* No Data Message - Show when tracking is disabled */
                <Paper sx={{ p: 3, mt: 3, backgroundColor: 'background.default' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CancelIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Geolocation Tracking Disabled
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This collection point has geolocation tracking disabled. No location data was collected during consent submission.
                        </Typography>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default GeolocationTabV2;
